import { Box, Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput from "./TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import useMaskedInput from "../../hooks/useMaskedInput";
import { cleanPhoneInput, displayPhone } from "../../utils/displayMask";
import {
  PHONE_COUNTRIES,
  isCompleteNationalNumber,
  phoneCountry,
} from "../../constants/phoneCountries";
import { dialCodeAdornment } from "./adornments";

/**
 * The mobile-money account the payment debits.
 *
 * ⛔ NOT built on `PhoneNumberInputs`. That component is hard-wired to
 * `usePayment()` and to customer-wallet logic an anonymous school-fee payer
 * never has -- it reads `customerUser.customer_wallet_collection` and writes
 * the selected wallet, neither of which exists on this journey.
 *
 * ⛔ NO `length === 9`. The country decides the national-number length, and
 * the country comes from a registry (constants/phoneCountries.ts) so a second
 * country is a data row rather than a change here.
 */
// `numberError` is the number-against-method refusal the template raises at
// Continue; it shows in place of the hint until the number or method changes.
const PayerPhoneInput = ({ numberError = "" }: { numberError?: string }) => {
  const { t } = useTranslation();
  const {
    instrumentAccountNumber,
    setInstrumentAccountNumber,
    instrumentCountry,
    setInstrumentCountry,
  } = useSchoolFee();

  const country = phoneCountry(instrumentCountry);
  const complete = isCompleteNationalNumber(
    instrumentAccountNumber,
    instrumentCountry
  );
  const invalid = instrumentAccountNumber !== "" && !complete;
  const masked = useMaskedInput({
    value: instrumentAccountNumber,
    clean: raw => cleanPhoneInput(raw, instrumentCountry),
    display: national => displayPhone(national, instrumentCountry),
    onValueChange: setInstrumentAccountNumber,
  });

  return (
    <Box mb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextInput
            select
            fullWidth
            id="schoolfee-phone-country"
            label={t("schoolfee.contact.country_label")}
            value={instrumentCountry}
            onChange={event => setInstrumentCountry(event.target.value)}
            variant="outlined"
          >
            {PHONE_COUNTRIES.map(entry => (
              <MenuItem key={entry.code} value={entry.code}>
                {`${entry.code} ${entry.dialCode}`}
              </MenuItem>
            ))}
          </TextInput>
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextInput
            fullWidth
            // Mandatory in fact already -- Continue is withheld without a
            // complete number -- and now shown so. The school's parent phone
            // below carries the marker its configuration asks for, and the two
            // read as disagreeing about what the form requires without this.
            required
            type="tel"
            inputMode="numeric"
            id="schoolfee-phone"
            slotProps={dialCodeAdornment(instrumentCountry)}
            label={t("schoolfee.contact.phone_label")}
            inputRef={masked.inputRef}
            value={masked.value}
            onChange={masked.onChange}
            error={invalid || !!numberError}
            helperText={
              numberError
                ? numberError
                : invalid
                ? // `length`, not `count` -- `count` is i18next's plural
                  // selector and would silently look for _one/_other keys.
                  t("schoolfee.contact.phone_length", {
                    length: country?.nationalLength ?? 0,
                  })
                : t("schoolfee.contact.phone_hint")
            }
            placeholder={
              country ? displayPhone(country.example, country.code) : undefined
            }
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayerPhoneInput;
